import { Observable, Subject } from 'rxjs';
import {  map, tap } from 'rxjs/operators';
import { UiNotificationService } from 'src/app/external-ui-shared/services/ui-notification.service';
import { CustomDialogSettings } from 'src/app/shared/model/CustomDialogSettings';
import { CustomDialogService } from 'src/app/shared/services/custom-dialog.service';
import { DatePipe } from '@angular/common';
import { Component, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DialogContentBase, DialogRef, DialogResult } from '@progress/kendo-angular-dialog';
import { DocumentInfo } from '../../models/DocumentInfo';
import { ExportDocumentInfo } from '../../models/ExportDocumentInfo';
import { ExportDocumentItem } from '../../models/ExportDocumentItem';
import { DocumentService } from '../../service/document.service';
import { ExportDocumentsService } from '../../service/export-documents.service';
import { LinkInfoBoxComponent } from '../link-info-box/link-info-box.component';
import { ShowDocumentComponent } from '../show-document/show-document.component';
import { ExportDocumentsToAudanetComponent } from 'src/app/feature-module/audanet-integration/components/export-documents-to-audanet/export-documents-to-audanet.component';
import { DocumentGenerationService } from '../../service/document-generation.service';
import { GenerateImageSetRequest } from '../../models/GenerateImageSetRequest';
import { FileTypeService } from 'src/app/shared/services/file-type.service';

@Component({
  selector: "app-export-document-list",
  templateUrl: "./export-document-list.component.html",
  styleUrls: ["./export-document-list.component.scss"],
})
export class ExportDocumentListComponent
  extends DialogContentBase
  implements OnInit
{
  constructor(
    public dialog: DialogRef,
    private exportDocumentsService: ExportDocumentsService,
    private fileTypeService: FileTypeService,
    private documentService: DocumentService,
    private documentGenerationService: DocumentGenerationService,
    private customDialogService: CustomDialogService,
    private translateService: TranslateService,
    private uiNotification: UiNotificationService
  ) {
    super(dialog);
  }
  linkToDisplay$: Subject<string>;
  selectedDocs$: Observable<string[]>;
  selectedDocsInfos$: Observable<ExportDocumentInfo[]>;
  exportDocumentItems: ExportDocumentItem[];
  isLoading: boolean;
  isFileGenerating: boolean;
  docsCount = 0;
  claimNumber:string;
  plateNumberOrVin:string;

  @ViewChild("exportListDialog", { read: ViewContainerRef })
  public containerRef: ViewContainerRef;

  @ViewChild("exportAsLink", { read: ViewContainerRef })
  public containerAsLinkRef: ViewContainerRef;

  ngOnInit(): void {
    this.loadData();
  }

  loadData(){
    this.isLoading = true;
    this.selectedDocs$ = this.exportDocumentsService.selectedDocs$;
    this.selectedDocs$.subscribe((r) => {
      this.exportDocumentItems = [];

      if (r?.length <= 0) {
        this.selectedDocsInfos$ = null;
        this.docsCount = 0;
        return;
      }

      this.selectedDocsInfos$ = this.documentService
        .getExportDocumentsData(r)
        .pipe(
          tap((exportData) => {
            this.isLoading = false;
            this.docsCount = r.length;
            this.plateNumberOrVin = exportData.plateNumberOrVin;
            this.claimNumber = exportData.claimNumber;

            exportData?.exportDocumentItems.forEach((d) => {
              this.exportDocumentItems.push({
                targetFileName: d.nameForExport,
                documentId: d.id,
                claimIdentifier: d.claimIdentifier,
                fileExtension: d.fileExtension,
                documentKindId: d.documentKindId,
              });
            });
          }),
          map((r) => r.exportDocumentItems)
        );
    });
  }

  cancel() {
    this.dialog.close({ text: "Cancel" });
  }

  confirm() {
    this.dialog.close({ text: "OK" });
    // this.documentService.storeProofDocument(this.addData).subscribe(
    //   r => {
    //     this.uiNotification.showSuccess('validations.documentsExportedSuccesfully');
    //     this.dialog.close({ text: 'OK' });
    //    });
  }

  exportAsZip() {
    const task = this.uiNotification.showRunningOperation(
      "operations.zipFileGenerationOngoing"
    );
    this.isFileGenerating = true;
    const datePipe: DatePipe = new DatePipe("pl-PL");
    let zipName = "Dokumentacja";
    if(this.claimNumber){
      zipName += "_" + this.claimNumber;
    }

     if(this.plateNumberOrVin){
      zipName += "_" + this.plateNumberOrVin;
    }

    zipName += "_" + datePipe.transform(new Date(), "yyyy_MM_dd_HH_mm_ss") + ".zip";

    this.documentService
      .exportDocumentsAsCompressedFile(this.exportDocumentItems)
      .subscribe((content) => {
        const downloadLink = document.createElement("a");
        downloadLink.href = window.URL.createObjectURL(
          new Blob([content], { type: "application/octet-stream" })
        );
        downloadLink.setAttribute(
          "download",
         zipName
        );
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.parentNode.removeChild(downloadLink);
        task.hide();
        this.isFileGenerating = false;
        if (this.dialog) {
          this.dialog.close({ text: "OK" });
        }
        this.exportDocumentsService.removeAllDocuments();
      });
  }

  downloadSingleFile(documentInfo: ExportDocumentInfo) {
    const task = this.uiNotification.showRunningOperation(
      "operations.singleFilesExportOngoing"
    );
    this.isFileGenerating = true;

    this.documentService
      .downloadDocument(documentInfo.id)
      .subscribe((content) => {
        const downloadLink = document.createElement("a");
        downloadLink.href = window.URL.createObjectURL(
          new Blob([content], { type: "application/octet-stream" })
        );
        downloadLink.setAttribute("download", documentInfo.nameForExport);
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.parentNode.removeChild(downloadLink);

        task.hide();
        this.isFileGenerating = false;
      });
  }

  generateImageSetPdf(){

    const notImages = this.exportDocumentItems.find( x => !this.fileTypeService.isFileImage(x.fileExtension));
    if(notImages){
      this.uiNotification.showError('errors.cantExportNonImagesFilesAsImageSet');
      return;
    }

    const datePipe: DatePipe = new DatePipe('pl-PL');

    const requestData : GenerateImageSetRequest = {
      claimIdentifier: this.exportDocumentItems[0].claimIdentifier,
      documentInfoIds: this.exportDocumentItems.map((value) => value.documentId)
    };

    const task = this.uiNotification.showRunningOperation('operations.documentGenerationIsOngoing');

    const pdfName = "Dokumentacja_zbiorcza_" + this.claimNumber + "_" + this.plateNumberOrVin + "_" + datePipe.transform(new Date(),'yyyy_MM_dd_HH_mm_ss')  + '.pdf';
    this.documentGenerationService.generateImageSetPdf(requestData).subscribe(
      r => {
        const downloadLink = document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(new Blob([r], { type: 'application/pdf' }));
        downloadLink.setAttribute('download',pdfName);
        document.body.appendChild(downloadLink);
        downloadLink.click();
        downloadLink.parentNode.removeChild(downloadLink);
        task.hide()
      }
    );
  }

  showContent(documentInfo: DocumentInfo) {
    const dialogSettings: CustomDialogSettings = {
      content: ShowDocumentComponent,
      title: `${this.translateService.instant(
        "claims-center.claimDocumentation.showContent"
      )}  (${documentInfo.name})`,
      minHeight: "60vh",
      minWidth: "60vw",
      maxHeight: "90vh",
      maxWidth: "90vw",
      appendTo: this.containerRef,
      dialogInputs: {
        documentInfo: documentInfo,
      },
    };

    this.customDialogService.showDialog(dialogSettings);
  }

  public showLinkInfoBox(): void {
   
        const dialogSettings: CustomDialogSettings = {
          content: LinkInfoBoxComponent,
          title: `${this.translateService.instant(
            "claims-center.claimDocumentation.linkInfo"
          )} `,
          minHeight: "40vh",
          minWidth: "50vw",
          maxHeight: "98vh",
          maxWidth: "98vw",
          appendTo: this.containerAsLinkRef,
          dialogInputs: {
            documentInfoIds:this.exportDocumentItems.map((value) => value.documentId),
            claimIdentifier: this.exportDocumentItems[0].claimIdentifier
          },
        };

        this.customDialogService.showDialog(dialogSettings);
  }

  exportToAudanet(){

    if(!this.exportDocumentItems?.length){
      return;
    }

      const dialogSettings: CustomDialogSettings = {
        content: ExportDocumentsToAudanetComponent,
        minHeight: "60vh",
        minWidth: "60vw",
        maxHeight: "90vh",
        maxWidth: "90vw",
        appendTo: this.containerAsLinkRef,
        dialogInputs: {
          exportDocumentItems: this.exportDocumentItems,
          claimIdentifier: this.exportDocumentItems[0].claimIdentifier
        },
        resultCallback: (r: DialogResult) => {        
            this.loadData();
        }
      };

      this.customDialogService.showDialog(dialogSettings);
    }

  removeFromExport(documentId: string) {
    this.exportDocumentsService.removeDocument(documentId);
  }

  hasDescription(description: string) {
    return !!description;
  }
}
